import {
  GetCertificationsOutput,
  useDeleteCertificationMutation,
} from '$store/modules/certifications';
import capitalizedName from '$utils/capitalizedName';
import { AdditionalDocument } from '$types/candidates';
import ModalInTailwind from '$components/ModalInTailwind/ModalInTailwind';
import { useState } from 'react';
import Button from '$components/Buttons/Button';
import { errorHandler, successHandler } from '$store/utils';
import { useTranslation } from 'react-i18next';

const CandidateCertificationsViewer = ({
  candidateCertifications,
  certificationFiles,
  onEdit,
}: {
  candidateCertifications: GetCertificationsOutput | undefined;
  certificationFiles: AdditionalDocument[];
  onEdit: (certification: GetCertificationsOutput[number]) => void;
}) => {
  const [t] = useTranslation('global');
  const [selectedDeleteCertificationId, setSelectedDeleteCertificationId] =
    useState<number | undefined>(undefined);

  const [deleteCertification] = useDeleteCertificationMutation();

  const getCertificationUrlDomain = (urlString: string) => {
    try {
      if (!/^https?:\/\//i.test(urlString)) {
        urlString = `https://${urlString}`;
      }

      const url = new URL(urlString);
      return capitalizedName(url.hostname.replace(/^www\./, ''));
    } catch (e) {
      return urlString;
    }
  };

  const handleDeleteCertification = async () => {
    await deleteCertification(selectedDeleteCertificationId as number).then(
      (resp) => {
        if ('error' in resp)
          return errorHandler(
            '[DeleteCandidateCertification]',
            'Something went wrong. Try again later',
            true,
            false
          );

        setSelectedDeleteCertificationId(undefined);
        return successHandler('Certification deleted successfully', true, true);
      }
    );
  };

  return (
    <div className="flex flex-col gap-8">
      {candidateCertifications &&
        candidateCertifications.map((singleCertification) => (
          <div
            className="flex flex-row items-center"
            key={singleCertification.Id}
          >
            <div className="flex flex-col w-2/6">
              <span className="font-semibold">
                {singleCertification.CertificationName}
              </span>
              <span className="text-xs">{singleCertification.Description}</span>
            </div>
            <div className="flex flex-row w-full justify-end items-center">
              <div className="flex flex-row gap-8 w-2/3">
                <p className="flex flex-col">
                  <span className="text-xs">
                    {t('CandidateCertifications.organization')}
                  </span>
                  <span className="font-semibold">
                    {singleCertification.IssuingOrganization || 'N/A'}
                  </span>
                </p>
                <p className="flex flex-col">
                  <span className="text-xs">
                    {t('CandidateCertifications.credentialID')}
                  </span>
                  <span className="font-semibold">
                    {singleCertification.CredentialId || 'N/A'}
                  </span>
                </p>

                <p className="flex flex-col">
                  <span className="text-xs">
                    {t('CandidateCertifications.credentialURL')}
                  </span>
                  <span className="font-semibold">
                    {singleCertification.CredentialUrl ? (
                      <a
                        href={singleCertification.CredentialUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="text-internal-link"
                        title={singleCertification.CredentialUrl}
                      >
                        {getCertificationUrlDomain(
                          singleCertification?.CredentialUrl || ''
                        )}
                      </a>
                    ) : (
                      'N/A'
                    )}
                  </span>
                </p>
                <p className="flex flex-col">
                  <span className="text-xs">
                    {t('CandidateCertifications.issueDate')}
                  </span>
                  <span>{singleCertification.IssueDate || 'N/A'}</span>
                </p>
                <p className="flex flex-col">
                  <span className="text-xs">
                    {t('CandidateCertifications.expiryDate')}
                  </span>
                  <span>{singleCertification.ExpiryDate || 'N/A'}</span>
                </p>
              </div>

              <div className="flex flex-row gap-4">
                <button
                  type="button"
                  className="flex flex-col justify-center items-center"
                  onClick={() => onEdit(singleCertification)}
                >
                  <span className="pi pi-pencil text-2xl text-internal-main" />
                  <span className="text-internal-main text-xs">
                    {t('CandidateCertifications.edit')}
                  </span>
                </button>
                <a
                  className="flex flex-col justify-center items-center"
                  target="_blank"
                  rel="noreferrer"
                  href={
                    certificationFiles.find(
                      (singleFile) =>
                        singleCertification.FileId === singleFile.Id
                    )?.Url
                  }
                >
                  <span className="pi pi-file-pdf text-2xl text-internal-main" />
                  <span className="text-internal-main text-xs">
                    {t('CandidateCertifications.download')}
                  </span>
                </a>
                <button
                  type="button"
                  className="flex flex-col justify-center items-center"
                  onClick={() =>
                    setSelectedDeleteCertificationId(singleCertification.Id)
                  }
                >
                  <span className="pi pi-trash text-2xl text-internal-main" />
                  <span className="text-internal-main text-xs">
                    {t('CandidateCertifications.delete')}
                  </span>
                </button>
              </div>
            </div>
          </div>
        ))}
      <ModalInTailwind
        isOpen={!!selectedDeleteCertificationId}
        closeModal={() => setSelectedDeleteCertificationId(undefined)}
      >
        <div className="bg-white rounded p-8 w-1/2 items-center justify-center flex flex-col">
          <h1 className="text-xl">
            Are you sure you want to delete this certification?
          </h1>
          <span>This action can&apos;t be reverted</span>
          <div className="flex flex-row gap-8 mt-4">
            <Button
              bgColor="#afafaf"
              showText
              text="Cancel"
              invertTextContent
              styling="px-4 whitespace-nowrap"
              onClick={() => setSelectedDeleteCertificationId(undefined)}
            />
            <Button
              bgColor="#079e88"
              showText
              text="Delete"
              styling="px-4 whitespace-nowrap"
              onClick={handleDeleteCertification}
            />
          </div>
        </div>
      </ModalInTailwind>
    </div>
  );
};

export default CandidateCertificationsViewer;
